import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, } from 'react-router-dom';
import BusinessReviews from "./components/BusinessReviews";
import BusinessImages from "./components/BusinessImages";
import BusinessExperience from "./components/BusinessExperience";
import BusinessServices from "./components/BusinessServices";
import Spinner from '../../../utils/Spinner';
import { useAuth } from "../../../context/AuthContext";
import ContactFormOverlay from "../../../components/messaging/ContactFormOverlay";
import { toast } from "react-toastify";
import { getImage } from "../../../utils/cloudinarySizing";

const BusinessProfilePublicView = ({
    data,
    isFavorited,
    favoriteCount,
    handleFavoriteToggle,
    id
}) => {


    const { role, accessToken } = useAuth();
    // Overlay state for enlarged profile photo
    const [isOverlayOpen, setOverlayOpen] = useState(false);
    const [isContactOpen, setIsContactOpen] = useState(false);
    const [isMessagePopupOpen, setMessagePopupOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const handleOverlayToggle = () => {
        setOverlayOpen(!isOverlayOpen);
    };

    const toggleContactMenu = () => {
        setIsContactOpen(prev => !prev);
    };

    const handleSendContactForm = () => {
        if (!accessToken || !role) {
            toast.info("Login or Sign Up to send a contact form");
            return;
        }
        setIsContactOpen(false);
        setMessagePopupOpen(true); // reuse your existing popup
    };

    const location = useLocation();

    const [activeComponent, setActiveComponent] = useState('Services');

    const [sliderIndex, setSliderIndex] = useState(0);


    const navigate = useNavigate();


    const canFavorite = role === "user";

    const tabs = [
        { name: "Services", icon: "fa-solid fa-wrench" },
        { name: "History", icon: "fa-solid fa-hourglass-half" },
        { name: "Images", icon: "fa-solid fa-image" },
        { name: "Reviews", icon: "fa-solid fa-star", style: { color: "#ffcc00" } },
    ];

    const handleTabClick = (tab, index) => {
        setActiveComponent(tab);
        setSliderIndex(index);
        navigate(`/business/${id}/${tab.toLowerCase()}`);
    };


    const renderComponent = () => {
        if (!data) return null; // wait until data is loaded

        switch (activeComponent) {
            case "Services":
                return <BusinessServices businessId={data?.profile?.id} />;
            case 'History':
                return <BusinessExperience data={data} />;
            case "Images":
                return <BusinessImages id={data.id} />;
            case 'Reviews':
                return <BusinessReviews businessId={data.id} />;
            default:
                return null;
        }
    };

    const renderTabButtons = () => (
        tabs.map((tab, index) => (
            <button
                key={tab.name}
                className={`nav-button ${activeComponent === tab.name ? "active" : ""}`}
                onClick={() => handleTabClick(tab.name, index)}
            >
                {tab.icon && <i className={tab.icon} style={tab.style}></i>}
                <span style={{ marginLeft: "8px" }}>
                    {/* Removed conditional check for "Media" to simplify rendering */}
                    {tab.name}
                </span>
            </button>
        ))
    );

    const formatLocation = (city, state) => {
        if (!city && !state) return null;
        if (city && state) return ` ${city}, ${state}`;
        return city || state;
    };

    const formatPhone = (value) => {
        const digits = value.replace(/\D/g, "").substring(0, 10);
        const part1 = digits.substring(0, 3);
        const part2 = digits.substring(3, 6);
        const part3 = digits.substring(6, 10);

        if (digits.length > 6) return `(${part1}) ${part2}-${part3}`;
        if (digits.length > 3) return `(${part1}) ${part2}`;
        if (digits.length > 0) return `(${part1}`;
        return "";
    };

    useEffect(() => {
        const current = location.pathname.split("/").pop();
        const match = tabs.findIndex(
            tab => tab.name.toLowerCase() === current.toLowerCase()
        );

        if (match !== -1) {
            setActiveComponent(tabs[match].name);
            setSliderIndex(match);
        } else {
            setActiveComponent("Services");
            setSliderIndex(0);
        }
    }, [location.pathname]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 900);
        handleResize(); // initialize
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const ContactButton = (
        <div className="contact-button-container">
            {/* Contact button */}
            <button
                className="contact-button"
                onClick={toggleContactMenu}
            >
                <i className="fa-solid fa-envelope" />
                <span>Contact</span>
                <i className="fa-solid fa-chevron-down caret" />
            </button>

            {isContactOpen && (
                <div className="contact-dropdown">
                    {/* Contact info */}
                    <div className="contact-info">
                        <p>
                            <i className="fa-solid fa-phone" />
                            <span>
                                Phone:{" "}
                                {data.profile?.phone_number
                                    ? formatPhone(data.profile.phone_number)
                                    : "Not Available"}
                            </span>
                        </p>

                        <p>
                            <i className="fa-solid fa-envelope" />
                            <span>
                                Email: {data.profile?.contact_email || "Not Available"}
                            </span>
                        </p>
                    </div>


                    {isMobile && (
                        <div className="business-links-row">
                            {/* Website */}
                            {data?.website && (
                                <a
                                    href={data.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="business-portfolio-icon"
                                    title="Website"
                                >
                                    <i className="fa-solid fa-link"></i>
                                </a>
                            )}

                            {/* Facebook */}
                            {data?.facebook && (
                                <a
                                    href={data.facebook}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="business-portfolio-icon"
                                    title="Facebook"
                                >
                                    <i className="fa-brands fa-facebook"></i>
                                </a>
                            )}

                            {/* Instagram */}
                            {data?.instagram && (
                                <a
                                    href={data.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="business-portfolio-icon"
                                    title="Instagram"
                                >
                                    <i className="fa-brands fa-instagram"></i>
                                </a>
                            )}

                            {/* X / Twitter */}
                            {data?.x && (
                                <a
                                    href={data.x}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="business-portfolio-icon"
                                    title="X"
                                >
                                    <i className="fa-brands fa-x-twitter"></i>
                                </a>
                            )}

                            {/* YouTube */}
                            {data?.youtube && (
                                <a
                                    href={data.youtube}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="business-portfolio-icon"
                                    title="YouTube"
                                >
                                    <i className="fa-brands fa-youtube"></i>
                                </a>
                            )}
                        </div>
                    )}

                    <div className="contact-divider" />

                    {/* CTA */}
                    <button
                        className="contact-form-button"
                        onClick={handleSendContactForm}
                    >
                        <i className="fa-solid fa-paper-plane" /> Send a Contact Form
                    </button>
                </div>
            )}
        </div>
    );


    return (
        <div className="business-page-wrapper">
            <div className="business-profile-container">
                <div className="centered-content">
                    <div className="business-profile-content">
                        <div className="photo-and-reviews">
                            <div className="business-profile-photo" onClick={handleOverlayToggle}>
                                <img
                                    src={getImage(data?.profile?.photo_original, "avatar_sm") || '/servspace-business-placeholder-200.png'}
                                    alt="Profile"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/servspace-business-placeholder.png';
                                    }}
                                />
                            </div>

                            {/* Overlay for Enlarged Image */}
                            {isOverlayOpen && (
                                <div className="image-overlay" onClick={handleOverlayToggle}>
                                    <img
                                        src={data?.profile?.photo_original || '/servspace-business-placeholder.png'}
                                        alt="Enlarged Profile"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '/servspace-business-placeholder.png';
                                        }}
                                    />
                                </div>
                            )}



                            <div className="business-name-icons-wrapper">
                                <div className="business-name-row">
                                    <h3 className="business-name">
                                        {data?.business_name || ""}
                                    </h3>
                                    <span className="business-handle">@{data?.username}</span>
                                </div>

                                <div className="business-icons">

                                    {!isMobile && ContactButton}

                                    {/* Location group */}
                                    {(data?.profile?.city || data?.profile?.state) && (
                                        <div className="freelancer-location">
                                            <i className="fa-solid fa-location-dot"></i>
                                            <span>
                                                {formatLocation(data.profile.city, data.profile.state)}
                                            </span>
                                        </div>
                                    )}

                                    {isMobile && (
                                        <div className="contact-button-mobile">
                                            {ContactButton}
                                        </div>
                                    )}

                                    {/* Divider — only show if location AND at least one link exists */}
                                    {(data?.profile?.city || data?.profile?.state) &&
                                        (data.portfolio_link ||
                                            data.facebook ||
                                            data.instagram ||
                                            data.x ||
                                            data.youtube) && (
                                            <span className="freelancer-divider">|</span>
                                        )}

                                    {!isMobile && (
                                        <div className="business-links-row">
                                            {/* Website */}
                                            {data?.website && (
                                                <a
                                                    href={data.website}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="business-portfolio-icon"
                                                    title="Website"
                                                >
                                                    <i className="fa-solid fa-link"></i>
                                                </a>
                                            )}

                                            {/* Facebook */}
                                            {data?.facebook && (
                                                <a
                                                    href={data.facebook}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="business-portfolio-icon"
                                                    title="Facebook"
                                                >
                                                    <i className="fa-brands fa-facebook"></i>
                                                </a>
                                            )}

                                            {/* Instagram */}
                                            {data?.instagram && (
                                                <a
                                                    href={data.instagram}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="business-portfolio-icon"
                                                    title="Instagram"
                                                >
                                                    <i className="fa-brands fa-instagram"></i>
                                                </a>
                                            )}

                                            {/* X / Twitter */}
                                            {data?.x && (
                                                <a
                                                    href={data.x}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="business-portfolio-icon"
                                                    title="X"
                                                >
                                                    <i className="fa-brands fa-x-twitter"></i>
                                                </a>
                                            )}

                                            {/* YouTube */}
                                            {data?.youtube && (
                                                <a
                                                    href={data.youtube}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="business-portfolio-icon"
                                                    title="YouTube"
                                                >
                                                    <i className="fa-brands fa-youtube"></i>
                                                </a>
                                            )}
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>

                        {isMobile ? (
                            // Mobile: wrap stats + heart together
                            <div className="stats-heart-wrapper-mobile">
                                {/* Heart button */}
                                <div className="heart-button-container">
                                    <button
                                        className={`save-button 
          ${isFavorited ? "favorited" : "unfavorited"} 
          ${!canFavorite ? "disabled" : ""}`}
                                        onClick={canFavorite ? handleFavoriteToggle : undefined}
                                        disabled={!canFavorite}
                                        title={!canFavorite ? "Only users can favorite businesses" : "Favorite"}
                                    >
                                        <i className="fa-solid fa-heart" />
                                        {favoriteCount > 0 && (
                                            <span className="favorite-count">{favoriteCount}</span>
                                        )}
                                    </button>
                                </div>

                                {/* Stats */}
                                <div className="stats-container">
                                    <div className="stat-item">
                                        <p>
                                            <i className="fa-solid fa-star"></i> {data?.average_rating}
                                        </p>
                                        <h4>Rating</h4>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            // Desktop: keep separate
                            <>
                                <div className="stats-container">
                                    <div className="stat-item">
                                        <p>
                                            <i className="fa-solid fa-star"></i> {data?.average_rating}
                                        </p>
                                        <h4>Rating</h4>
                                    </div>
                                </div>

                                <div className="heart-button-container">
                                    <button
                                        className={`save-button 
          ${isFavorited ? "favorited" : "unfavorited"} 
          ${!canFavorite ? "disabled" : ""}`}
                                        onClick={canFavorite ? handleFavoriteToggle : undefined}
                                        disabled={!canFavorite}
                                        title={!canFavorite ? "Only users can favorite businesses" : "Favorite"}
                                    >
                                        <i className="fa-solid fa-heart" />
                                        {favoriteCount > 0 && (
                                            <span className="favorite-count">{favoriteCount}</span>
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="container">
                        <div className="button-row">
                            {/* Sliding Bar */}
                            <div
                                className="freelancer-slider"
                                style={{
                                    width: `calc(100% / ${tabs.length})`,
                                    transform: `translateX(${sliderIndex * 100}%)`,
                                }}
                            />
                            {renderTabButtons()}
                        </div>
                        {renderComponent()}
                    </div>
                </div>
                <ContactFormOverlay
                    isOpen={isMessagePopupOpen}
                    onClose={() => setMessagePopupOpen(false)}
                    id={data?.profile?.id}
                    role={data?.role}
                />
            </div>
        </div>
    );
};

export default BusinessProfilePublicView;